import type { Page } from './types'

export type PageSwitcherOptions<R> = {
  /** Builds a page for a route. */
  readonly create: (route: R) => Page
  /** Routes whose page is kept alive (suspended, not destroyed) when the user leaves. */
  readonly keepAlive: (route: R) => boolean
  /** Two routes that should share one kept-alive page return the same key. */
  readonly key: (route: R) => string
}

export type PageSwitcher<R> = {
  readonly show: (route: R) => Page
  readonly current: () => Page | null
  readonly dispose: () => void
}

/**
 * Swaps pages on navigation. Kept-alive pages (the workbench, with its draft
 * and keystroke log) are suspended and resumed instead of rebuilt, so Back
 * never throws away what someone typed.
 */
export const createPageSwitcher = <R>(options: PageSwitcherOptions<R>): PageSwitcher<R> => {
  const kept = new Map<string, Page>()
  let current: Page | null = null

  const leave = (): void => {
    if (!current) return
    if ([...kept.values()].includes(current)) current.suspend?.()
    else current.destroy()
    current = null
  }

  return {
    show: (route) => {
      leave()
      if (options.keepAlive(route)) {
        const key = options.key(route)
        const existing = kept.get(key)
        const page = existing ?? options.create(route)
        if (!existing) kept.set(key, page)
        else page.resume?.()
        current = page
        return page
      }
      current = options.create(route)
      return current
    },
    current: () => current,
    dispose: () => {
      leave()
      for (const page of kept.values()) page.destroy()
      kept.clear()
    },
  }
}
